import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Assignment as AssignmentIcon,
  Input as InputIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useCart } from '../contexts/CartContext';
import InventoryForm from '../components/inventory/InventoryForm';

const CATEGORIES = [
  'Materials',
  'Tools',
  'Equipment',
  'Supplies',
  'Parts',
  'Other'
];

export default function Inventory() {
  const navigate = useNavigate();
  const { inventory, deleteInventoryItem, inventoryTransactions } = useData();
  const { addToCart, getCartItemCount } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [addToCartDialog, setAddToCartDialog] = useState({ open: false, item: null, quantity: 1 });
  const [checkInItems, setCheckInItems] = useState([]);

  const filteredInventory = inventory.map(item => {
    const totalQty = item.totalQuantity || item.quantity || 0;
    const checkedOut = item.checkedOutQuantity || 0;
    const available = totalQty - checkedOut;
    return { ...item, available, totalQty, checkedOut };
  }).filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    const matchesLowStock = !lowStockFilter || (item.available <= (item.lowStockThreshold || 10));
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const lowStockItems = inventory.filter(item => {
    const totalQty = item.totalQuantity || item.quantity || 0;
    const checkedOut = item.checkedOutQuantity || 0;
    const available = totalQty - checkedOut;
    return available <= (item.lowStockThreshold || 10);
  });

  const handleAdd = () => {
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setOpenDialog(true);
  };

  const handleDelete = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const confirmDelete = async () => {
    try {
      await deleteInventoryItem(deleteDialog.item.id);
      setDeleteDialog({ open: false, item: null });
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const handleAddToCart = (item) => {
    const totalQty = item.totalQuantity || item.quantity || 0;
    const checkedOut = item.checkedOutQuantity || 0;
    const available = totalQty - checkedOut;
    setAddToCartDialog({ open: true, item, quantity: 1, available });
  };

  const confirmAddToCart = () => {
    if (addToCartDialog.quantity > 0 && addToCartDialog.quantity <= addToCartDialog.available) {
      addToCart(addToCartDialog.item, addToCartDialog.quantity);
      setAddToCartDialog({ open: false, item: null, quantity: 1, available: 0 });
    }
  };

  const getStockStatus = (item) => {
    const available = item.available || 0;
    const threshold = item.lowStockThreshold || 10;
    if (available === 0) return { label: 'Out of Stock', color: 'error' };
    if (available <= threshold) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  // Calculate items that are checked out via pick lists, grouped by pick list number
  useEffect(() => {
    const checkedOutByPickList = {};
    
    inventory.forEach(item => {
      const pickListCheckouts = inventoryTransactions.filter(
        t => t.itemId === item.id && t.jobId === 'pick-list' && t.type === 'checkout' && t.pickListNumber
      );
      const pickListCheckins = inventoryTransactions.filter(
        t => t.itemId === item.id && t.jobId === 'pick-list' && t.type === 'checkin' && t.pickListNumber
      );
      
      // Group by pick list number
      pickListCheckouts.forEach(checkout => {
        const plNumber = checkout.pickListNumber || 'Unknown';
        if (!checkedOutByPickList[plNumber]) {
          checkedOutByPickList[plNumber] = [];
        }
        
        // Calculate net checked out for this item on this pick list
        const checkinsForThisPL = pickListCheckins
          .filter(cin => cin.pickListNumber === plNumber)
          .reduce((sum, cin) => sum + (cin.quantity || 0), 0);
        const netCheckedOut = (checkout.quantity || 0) - checkinsForThisPL;
        
        if (netCheckedOut > 0) {
          const existing = checkedOutByPickList[plNumber].find(entry => entry.item.id === item.id);
          if (existing) {
            existing.checkedOutQty += netCheckedOut;
          } else {
            checkedOutByPickList[plNumber].push({
              item,
              checkedOutQty: netCheckedOut,
              pickListNumber: plNumber
            });
          }
        }
      });
    });
    
    // Flatten and sort by pick list number
    const flattened = Object.entries(checkedOutByPickList).flatMap(([plNumber, items]) =>
      items.map(item => ({ ...item, pickListNumber: plNumber }))
    );
    
    setCheckInItems(flattened);
  }, [inventory, inventoryTransactions]);


  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            Inventory
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/inventory/pick-list')}
            sx={{ 
              position: 'relative',
              width: { xs: '50%', sm: 'auto' }
            }}
          >
            Pick List
            {getCartItemCount() > 0 && (
              <Chip
                label={getCartItemCount()}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  height: 20,
                  minWidth: 20,
                  fontSize: '0.75rem'
                }}
              />
            )}
          </Button>
          {checkInItems.length > 0 && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<InputIcon />}
              onClick={() => navigate('/inventory/check-in')}
              sx={{ width: { xs: '50%', sm: 'auto' } }}
            >
              Check Items In ({checkInItems.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ width: { xs: '50%', sm: 'auto' } }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {lowStockItems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          {lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''} {lowStockItems.length !== 1 ? 'are' : 'is'} running low on stock
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Category"
            >
              <MenuItem value="all">All Categories</MenuItem>
              {CATEGORIES.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant={lowStockFilter ? 'contained' : 'outlined'}
            color={lowStockFilter ? 'warning' : 'primary'}
            onClick={() => setLowStockFilter(!lowStockFilter)}
            startIcon={<WarningIcon />}
          >
            Low Stock Only
          </Button>
        </Box>
      </Paper>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ minWidth: 900 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Name</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>SKU</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Category</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Total</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Available</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}><strong>Checked Out</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Status</strong></TableCell>
              <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}><strong>Location</strong></TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm || categoryFilter !== 'all' || lowStockFilter
                      ? 'No items match your filters'
                      : 'No inventory items yet. Click "Add Item" to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredInventory.map((item) => {
                const stockStatus = getStockStatus(item);
                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/inventory/${item.id}`)}
                  >
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.sku || '-'}</TableCell>
                    <TableCell>
                      <Chip label={item.category} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {item.totalQty || 0}
                        {item.unit && ` ${item.unit}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {item.available || 0}
                        {item.unit && ` ${item.unit}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.checkedOut || 0}
                        {item.unit && ` ${item.unit}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stockStatus.label}
                        color={stockStatus.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{item.location || '-'}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {item.available > 0 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(item);
                          }}
                          color="success"
                          title="Add to Pick List"
                        >
                          <AddShoppingCartIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                        }}
                        color="primary"
                        title="Edit Item"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item);
                        }}
                        color="error"
                        title="Delete Item"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <InventoryForm
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingItem(null);
        }}
        item={editingItem}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
      >
        <DialogTitle>Delete Inventory Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.item?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addToCartDialog.open}
        onClose={() => setAddToCartDialog({ open: false, item: null, quantity: 1, available: 0 })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add to Pick List</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" fontWeight="medium">
              {addToCartDialog.item?.name}
            </Typography>
            {addToCartDialog.item?.location && (
              <Typography variant="body2" color="text.secondary">
                Location: {addToCartDialog.item.location}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={addToCartDialog.quantity}
              onChange={(e) => {
                const qty = parseFloat(e.target.value) || 0;
                setAddToCartDialog(prev => ({ ...prev, quantity: qty }));
              }}
              inputProps={{ min: 1, max: addToCartDialog.available, step: 1 }}
              helperText={`Available: ${addToCartDialog.available} ${addToCartDialog.item?.unit || 'units'}`}
            />
            {addToCartDialog.quantity > addToCartDialog.available && (
              <Alert severity="error">
                Cannot add more than available quantity
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToCartDialog({ open: false, item: null, quantity: 1, available: 0 })}>
            Cancel
          </Button>
          <Button
            onClick={confirmAddToCart}
            variant="contained"
            disabled={addToCartDialog.quantity <= 0 || addToCartDialog.quantity > addToCartDialog.available}
            startIcon={<AddShoppingCartIcon />}
          >
            Add to Pick List
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

